import React, { useState, useEffect } from "react";
import {
  X,
  Save,
  Calendar,
  Clock,
  User,
  FileText,
  AlertCircle,
  CheckCircle,
  Link,
  Plus,
  Copy,
  ExternalLink,
} from "lucide-react";
import { updateAppointment } from "../../../services/appointmentApi";

const EditAppointmentModal = ({
  isOpen,
  onClose,
  appointment,
  onSuccess,
  onError,
}) => {
  const [formData, setFormData] = useState({
    type: "",
    date: "",
    time: "",
    status: "",
    notes: "",
    duration: "",
    meetLink: "", // Add this field
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Appointment types
  const appointmentTypes = [
    "checkup",
    "vaccination",
    "surgery",
    "emergency",
    "grooming",
    "dental",
    "consultation",
  ];

  // Status options
  const statusOptions = [
    {
      value: "pending",
      label: "Pending",
      color: "bg-yellow-100 text-yellow-800",
    },
    {
      value: "confirmed",
      label: "Confirmed",
      color: "bg-blue-100 text-blue-800",
    },
    {
      value: "completed",
      label: "Completed",
      color: "bg-green-100 text-green-800",
    },
    {
      value: "cancelled",
      label: "Cancelled",
      color: "bg-red-100 text-red-800",
    },
  ];

  // Duration options
  const durationOptions = [
    "15 minutes",
    "30 minutes",
    "45 minutes",
    "1 hour",
    "1.5 hours",
    "2 hours",
  ];

  // Initialize form data when appointment changes
  useEffect(() => {
    if (appointment && isOpen) {
      setFormData({
        type: appointment.type || "",
        date: appointment.date || "",
        time: appointment.time || "",
        status: appointment.status || "",
        notes: appointment.notes || "",
        duration: appointment.duration || "30 minutes",
        meetLink: appointment.meetLink || "", // Add this line
      });
      setErrors({});
    }
  }, [appointment, isOpen]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Validate Google Meet link
  const isValidMeetLink = (url) => {
    if (!url || !url.trim()) return true; // Allow empty links
    const meetPattern = /^https:\/\/meet\.google\.com\/[a-z0-9\-]+$/i;
    // return meetPattern.test(url.trim());
    return true; // Temporarily disable validation to allow all links
  };

  // Handle generate meet link
  const handleGenerateMeetLink = () => {
    window.open("https://meet.google.com/new", "_blank");
    onSuccess(
      "Create a meeting in the new tab, then copy and paste the link here"
    );
  };

  // Handle copy meet link
  const handleCopyMeetLink = () => {
    if (formData.meetLink) {
      navigator.clipboard.writeText(formData.meetLink);
      onSuccess("Meeting link copied to clipboard!");
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.type.trim()) {
      newErrors.type = "Appointment type is required";
    }

    if (!formData.date) {
      newErrors.date = "Date is required";
    } else {
      // Check if date is not in the past (except if it's today)
      const selectedDate = new Date(formData.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        newErrors.date = "Date cannot be in the past";
      }
    }

    if (!formData.time) {
      newErrors.time = "Time is required";
    } else if (formData.date) {
      // Check if time is not in the past (if date is today)
      const selectedDate = new Date(formData.date);
      const today = new Date();

      if (selectedDate.toDateString() === today.toDateString()) {
        const [hours, minutes] = formData.time.split(":");
        const selectedTime = new Date();
        selectedTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

        if (selectedTime < new Date()) {
          newErrors.time = "Time cannot be in the past for today's date";
        }
      }
    }

    if (!formData.status) {
      newErrors.status = "Status is required";
    }

    // Validate meet link
    if (formData.meetLink && !isValidMeetLink(formData.meetLink)) {
      newErrors.meetLink =
        "Please enter a valid Google Meet link (https://meet.google.com/xxx-xxxx-xxx)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const updateData = {
        type: formData.type,
        date: formData.date,
        time: formData.time,
        status: formData.status,
        notes: formData.notes.trim() || undefined,
        duration: formData.duration,
        meetLink: formData.meetLink.trim() || null, // Add this line
      };

      const response = await updateAppointment(appointment._id, updateData);

      if (response.success) {
        onSuccess(response.message || "Appointment updated successfully!");
        console.log("Updated data is ", formData);
        onClose();
      } else {
        onError(response.message || "Failed to update appointment");
      }
    } catch (error) {
      console.error("Error updating appointment:", error);
      onError(
        typeof error === "string"
          ? error
          : "Failed to update appointment. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (loading) return;
    setFormData({
      type: "",
      date: "",
      time: "",
      status: "",
      notes: "",
      duration: "",
      meetLink: "", // Add this line
    });
    setErrors({});
    onClose();
  };

  if (!isOpen || !appointment) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center">
                <FileText className="w-6 h-6 mr-2" />
                Edit Appointment
              </h2>
              <p className="text-blue-100 mt-1">
                Update appointment details for{" "}
                {appointment.pets?.name || "Unknown Pet"}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-all duration-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="max-h-[calc(90vh-120px)] overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Pet Information (Read-only) */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Pet Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pet Name
                  </label>
                  <p className="text-gray-900 bg-white p-2 rounded border">
                    {appointment.pets?.name || "Unknown Pet"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Owner
                  </label>
                  <p className="text-gray-900 bg-white p-2 rounded border">
                    {appointment.pets?.ownerId?.userDetails?.name ||
                      appointment.pets?.owner?.name ||
                      "Unknown Owner"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Species
                  </label>
                  <p className="text-gray-900 bg-white p-2 rounded border">
                    {appointment.pets?.species || "Not specified"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Breed
                  </label>
                  <p className="text-gray-900 bg-white p-2 rounded border">
                    {appointment.pets?.breed || "Not specified"}
                  </p>
                </div>
              </div>
            </div>

            {/* Editable Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Appointment Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Appointment Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => handleInputChange("type", e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.type ? "border-red-300" : "border-gray-300"
                  }`}
                  disabled={loading}
                >
                  <option value="">Select appointment type</option>
                  {appointmentTypes.map((type) => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
                {errors.type && (
                  <p className="text-red-600 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.type}
                  </p>
                )}
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status *
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange("status", e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.status ? "border-red-300" : "border-gray-300"
                  }`}
                  disabled={loading}
                >
                  <option value="">Select status</option>
                  {statusOptions.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
                {errors.status && (
                  <p className="text-red-600 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.status}
                  </p>
                )}
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleInputChange("date", e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.date ? "border-red-300" : "border-gray-300"
                    }`}
                    disabled={loading}
                  />
                </div>
                {errors.date && (
                  <p className="text-red-600 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.date}
                  </p>
                )}
              </div>

              {/* Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time *
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="time"
                    value={formData.time}
                    onChange={(e) => handleInputChange("time", e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.time ? "border-red-300" : "border-gray-300"
                    }`}
                    disabled={loading}
                  />
                </div>
                {errors.time && (
                  <p className="text-red-600 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.time}
                  </p>
                )}
              </div>

              {/* Duration */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration
                </label>
                <select
                  value={formData.duration}
                  onChange={(e) =>
                    handleInputChange("duration", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                >
                  {durationOptions.map((duration) => (
                    <option key={duration} value={duration}>
                      {duration}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Google Meet Link Section */}
            <div className="bg-blue-50 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Link className="w-4 h-4 inline mr-1" />
                Meet Link (Optional)
              </label>
              <div className="space-y-3">
                <div className="relative">
                  <input
                    type="url"
                    placeholder="https://meet.google.com/xxx-xxxx-xxx"
                    value={formData.meetLink}
                    onChange={(e) =>
                      handleInputChange("meetLink", e.target.value)
                    }
                    className={`w-full px-3 py-2 pr-20 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.meetLink ? "border-red-300" : "border-gray-300"
                    }`}
                    disabled={loading}
                  />
                  {formData.meetLink && (
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex gap-1">
                      <button
                        type="button"
                        onClick={handleCopyMeetLink}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Copy Link"
                      >
                        <Copy className="w-4 h-4" />
                      </button>

                      <a
                        href={formData.meetLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Open Meeting"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  )}
                </div>
                {errors.meetLink && (
                  <p className="text-red-600 text-sm flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.meetLink}
                  </p>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleGenerateMeetLink}
                    className="flex items-center gap-1 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                    disabled={loading}
                  >
                    <Plus className="w-4 h-4" />
                    Generate New Link
                  </button>
                  {formData.meetLink && (
                    <button
                      type="button"
                      onClick={() => handleInputChange("meetLink", "")}
                      className="px-3 py-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 text-sm"
                      disabled={loading}
                    >
                      Clear Link
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-600">
                  Click "Generate New Link" to create a meeting in Google Meet,
                  then copy and paste the link here.
                </p>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                placeholder="Add any additional notes about the appointment..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                disabled={loading}
              />
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex flex-col-reverse sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="w-full sm:w-auto px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Update Appointment
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Changes Summary */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-3">
                Summary of Changes
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {formData.date !== appointment.date && (
                  <div>
                    <span className="text-gray-600">Date: </span>
                    <span className="line-through text-red-600">
                      {appointment.date}
                    </span>
                    <span className="text-green-600"> → {formData.date}</span>
                  </div>
                )}
                {formData.time !== appointment.time && (
                  <div>
                    <span className="text-gray-600">Time: </span>
                    <span className="line-through text-red-600">
                      {appointment.time}
                    </span>
                    <span className="text-green-600"> → {formData.time}</span>
                  </div>
                )}
                {formData.type !== appointment.type && (
                  <div>
                    <span className="text-gray-600">Type: </span>
                    <span className="line-through text-red-600">
                      {appointment.type}
                    </span>
                    <span className="text-green-600"> → {formData.type}</span>
                  </div>
                )}
                {formData.status !== appointment.status && (
                  <div>
                    <span className="text-gray-600">Status: </span>
                    <span className="line-through text-red-600">
                      {appointment.status}
                    </span>
                    <span className="text-green-600"> → {formData.status}</span>
                  </div>
                )}
                {formData.meetLink !== (appointment.meetLink || "") && (
                  <div>
                    <span className="text-gray-600">Meet Link: </span>
                    <span className="line-through text-red-600">
                      {appointment.meetLink ? "Set" : "Not set"}
                    </span>
                    <span className="text-green-600">
                      {" "}
                      → {formData.meetLink ? "Updated" : "Removed"}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditAppointmentModal;
